import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, IconButton,
  Tooltip as MuiTooltip, Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress,
} from '@mui/material';
import {
  ComposedChart, Bar, Cell, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Assessment, TrendingUp, TrendingDown, Remove, AutoAwesome } from '@mui/icons-material';
import { financeApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

const STATUS = {
  critical: { bg: '#FFEBEE', text: '#B71C1C', chipColor: '#EF5350', label: 'Critical'    },
  over:     { bg: '#FFF3E0', text: '#E65100', chipColor: '#FF9800', label: 'Over budget'  },
  at_risk:  { bg: '#FFFDE7', text: '#F57F17', chipColor: '#FFC107', label: 'At risk'      },
  on_track: { bg: '#F1F8E9', text: '#2E7D32', chipColor: '#66BB6A', label: 'On track'     },
  under:    { bg: '#E3F2FD', text: '#1565C0', chipColor: '#42A5F5', label: 'Under budget' },
};

const fmtRM = (v) => {
  if (v == null) return '—';
  return v >= 1e6 ? `RM ${(v / 1e6).toFixed(1)}M` : `RM ${(v / 1e3).toFixed(0)}K`;
};

const fmtPct = (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;

const STUB = {
  forecastSummary: 'Payroll and medical claims are tracking above budget (+3.2% YTD). At the current burn rate, projected year-end overspend is RM 1.24M — primarily driven by Medical Claims (+20%) and Payroll (+4.3%).',
  ytdVariancePct: 3.2,
  projectedYearEndVarianceRM: 1240000,
  monthlyBudget: 4600000,
  monthlyForecast: [
    { month: 'Jan', actual: 4750000, budget: 4600000, forecast: null,    status: 'over'     },
    { month: 'Feb', actual: 4680000, budget: 4600000, forecast: null,    status: 'over'     },
    { month: 'Mar', actual: 4820000, budget: 4600000, forecast: null,    status: 'over'     },
    { month: 'Apr', actual: 4540000, budget: 4600000, forecast: null,    status: 'on_track' },
    { month: 'May', actual: 4910000, budget: 4600000, forecast: null,    status: 'over'     },
    { month: 'Jun', actual: 4800000, budget: 4600000, forecast: null,    status: 'over'     },
    { month: 'Jul', actual: null,    budget: 4600000, forecast: 4790000, status: 'at_risk'  },
    { month: 'Aug', actual: null,    budget: 4600000, forecast: 4760000, status: 'at_risk'  },
    { month: 'Sep', actual: null,    budget: 4600000, forecast: 4720000, status: 'at_risk'  },
    { month: 'Oct', actual: null,    budget: 4600000, forecast: 4700000, status: 'at_risk'  },
    { month: 'Nov', actual: null,    budget: 4600000, forecast: 4660000, status: 'on_track' },
    { month: 'Dec', actual: null,    budget: 4600000, forecast: 4640000, status: 'on_track' },
  ],
  categoryVariances: [
    { category: 'Medical Claims', ytdActual: 1440000,  ytdBudget: 1200000,  variancePct: 20.0,  forecastYearEnd: 2880000,  budgetYearEnd: 2400000,  status: 'critical', trend: 'increasing' },
    { category: 'Payroll',        ytdActual: 28800000, ytdBudget: 27600000, variancePct: 4.3,   forecastYearEnd: 57600000, budgetYearEnd: 55200000, status: 'over',     trend: 'stable'    },
    { category: 'Allowances',     ytdActual: 780000,   ytdBudget: 720000,   variancePct: 8.3,   forecastYearEnd: 1560000,  budgetYearEnd: 1440000,  status: 'over',     trend: 'stable'    },
    { category: 'Overtime',       ytdActual: 960000,   ytdBudget: 1080000,  variancePct: -11.1, forecastYearEnd: 1920000,  budgetYearEnd: 2160000,  status: 'under',    trend: 'decreasing'},
    { category: 'Training',       ytdActual: 240000,   ytdBudget: 300000,   variancePct: -20.0, forecastYearEnd: 480000,   budgetYearEnd: 600000,   status: 'under',    trend: 'stable'    },
  ],
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0]?.payload;
  const val = entry?.actual ?? entry?.forecast;
  if (val == null) return null;
  const isActual = entry?.actual != null;
  const diff = val - entry.budget;
  const diffPct = ((diff / entry.budget) * 100).toFixed(1);
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #E0E0E0', borderRadius: 1.5, p: 1.5, minWidth: 190, boxShadow: 1 }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>{label}</Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        {isActual ? 'Actual' : 'Forecast'}: <strong>{fmtRM(val)}</strong>
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        Monthly budget: {fmtRM(entry.budget)}
      </Typography>
      <Typography variant="caption" display="block" fontWeight={700}
        sx={{ color: diff > 0 ? '#EF5350' : '#2E7D32', mt: 0.5 }}>
        {diff > 0 ? '▲' : '▼'} {fmtRM(Math.abs(diff))} ({diff > 0 ? '+' : ''}{diffPct}%)
      </Typography>
    </Box>
  );
}

function TrendIcon({ trend }) {
  if (trend === 'increasing') return <TrendingUp sx={{ fontSize: 16, color: '#EF5350', verticalAlign: 'middle' }} />;
  if (trend === 'decreasing') return <TrendingDown sx={{ fontSize: 16, color: '#1565C0', verticalAlign: 'middle' }} />;
  return <Remove sx={{ fontSize: 16, color: '#9E9E9E', verticalAlign: 'middle' }} />;
}

export default function BudgetForecastPanel() {
  const { openWithMessage } = useChatContext();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    financeApi.getBudgetForecast()
      .then(setData)
      .catch(() => setData(STUB))
      .finally(() => setLoading(false));
  }, []);

  const d = data || STUB;

  const summaryBg    = d.ytdVariancePct > 10 ? '#FFEBEE' : d.ytdVariancePct > 0 ? '#FFF3E0' : '#F1F8E9';
  const summaryColor = d.ytdVariancePct > 10 ? '#B71C1C' : d.ytdVariancePct > 0 ? '#E65100' : '#2E7D32';

  const chartData = d.monthlyForecast.map((m) => ({
    ...m,
    actual:   m.actual   != null ? m.actual   : undefined,
    forecast: m.forecast != null ? m.forecast : undefined,
  }));

  const atRiskCats = d.categoryVariances.filter((c) => c.status === 'critical' || c.status === 'over');
  const onTrackCount = d.categoryVariances.filter((c) => c.status === 'on_track' || c.status === 'under').length;

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#1565C018', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Assessment sx={{ fontSize: 20, color: '#1565C0' }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>Budget Variance Forecasting</Typography>
          </Box>
          <MuiTooltip title="Ask AI about overall budget outlook">
            <IconButton size="small"
              sx={{ color: 'primary.main', opacity: 0.6, '&:hover': { opacity: 1 } }}
              onClick={() => openWithMessage(
                `Budget variance is ${fmtPct(d.ytdVariancePct)} YTD with a projected year-end ` +
                `${d.projectedYearEndVarianceRM > 0 ? 'overspend' : 'saving'} of ${fmtRM(Math.abs(d.projectedYearEndVarianceRM))}. ` +
                (atRiskCats.length
                  ? `Categories at risk: ${atRiskCats.map((c) => `${c.category} (${fmtPct(c.variancePct)})`).join(', ')}. `
                  : '') +
                `What cost controls should Finance and HR implement in H2 to close the gap?`
              )}>
              <AutoAwesome sx={{ fontSize: 18 }} />
            </IconButton>
          </MuiTooltip>
        </Box>

        {/* AI summary bar */}
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">Loading forecast…</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: summaryBg, border: `1px solid ${summaryColor}30`, borderRadius: 1.5, px: 1.5, py: 1, mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: summaryColor, fontStyle: 'italic' }}>{d.forecastSummary}</Typography>
          </Box>
        )}

        {/* KPI chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`YTD Variance: ${fmtPct(d.ytdVariancePct)}`}
            size="small"
            sx={{ bgcolor: d.ytdVariancePct > 0 ? '#FFF3E0' : '#F1F8E9', color: d.ytdVariancePct > 0 ? '#E65100' : '#2E7D32', fontWeight: 700 }}
          />
          <Chip
            label={`Year-end ${d.projectedYearEndVarianceRM > 0 ? 'Overspend' : 'Saving'}: ${fmtRM(Math.abs(d.projectedYearEndVarianceRM))}`}
            size="small"
            sx={{ bgcolor: d.projectedYearEndVarianceRM > 0 ? '#FFEBEE' : '#F1F8E9', color: d.projectedYearEndVarianceRM > 0 ? '#B71C1C' : '#2E7D32', fontWeight: 700 }}
          />
          <Chip
            label={`${onTrackCount}/${d.categoryVariances.length} categories on/under budget`}
            size="small"
            sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600 }}
          />
        </Box>

        {/* Combination chart — actual bars (past) + forecast bars (future) + budget ref line */}
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 60, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : `${(v / 1e3).toFixed(0)}K`}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend formatter={(value) => <span style={{ fontSize: 12 }}>{value}</span>} />
            <ReferenceLine
              y={d.monthlyBudget}
              stroke="#1565C0"
              strokeDasharray="6 3"
              strokeWidth={1.5}
              label={{ value: 'Budget', position: 'right', fontSize: 11, fill: '#1565C0' }}
            />
            <Bar dataKey="actual" name="Actual" maxBarSize={26} radius={[3,3,0,0]}>
              {chartData.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.actual == null
                    ? 'transparent'
                    : entry.actual > entry.budget ? '#EF5350' : '#1565C0'}
                />
              ))}
            </Bar>
            <Bar dataKey="forecast" name="Forecast" maxBarSize={26} radius={[3,3,0,0]}>
              {chartData.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.forecast == null
                    ? 'transparent'
                    : entry.forecast > entry.budget ? '#FFA726' : '#42A5F5'}
                  fillOpacity={0.75}
                />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>

        {/* Category breakdown table */}
        <Typography variant="subtitle2" fontWeight={700} mt={2.5} mb={1}>Category Breakdown</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Category', 'YTD Actual', 'YTD Budget', 'Variance', 'YE Forecast', 'YE Budget', 'Status', 'Trend', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.73rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {d.categoryVariances.map((row) => {
                const s = STATUS[row.status] || STATUS.on_track;
                return (
                  <TableRow key={row.category} sx={{ bgcolor: s.bg, '&:hover': { filter: 'brightness(0.97)' } }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{row.category}</TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtRM(row.ytdActual)}</TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtRM(row.ytdBudget)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: s.text, whiteSpace: 'nowrap' }}>{fmtPct(row.variancePct)}</TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtRM(row.forecastYearEnd)}</TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtRM(row.budgetYearEnd)}</TableCell>
                    <TableCell>
                      <Chip
                        label={s.label}
                        size="small"
                        sx={{ bgcolor: s.chipColor, color: '#fff', fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TrendIcon trend={row.trend} />
                    </TableCell>
                    <TableCell>
                      <MuiTooltip title={`Ask AI about ${row.category} variance`}>
                        <IconButton size="small"
                          sx={{ color: 'primary.main', opacity: 0.55, '&:hover': { opacity: 1 }, p: 0.5 }}
                          onClick={() => openWithMessage(
                            `The ${row.category} budget has a YTD variance of ${fmtPct(row.variancePct)} ` +
                            `(actual ${fmtRM(row.ytdActual)} vs budget ${fmtRM(row.ytdBudget)}). ` +
                            `Year-end projection is ${fmtRM(row.forecastYearEnd)} against a budget of ${fmtRM(row.budgetYearEnd)}. ` +
                            `The trend is ${row.trend}. What is driving this and what specific actions should we take?`
                          )}>
                          <AutoAwesome sx={{ fontSize: 14 }} />
                        </IconButton>
                      </MuiTooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
}
